import { useEffect } from 'react';
import logo from '../logo.png';
import './Home.css';

function SmileyIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
      <path d="M8 14s1.5 2 4 2 4-2 4-2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="9" y1="9" x2="9.01" y2="9" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
      <line x1="15" y1="9" x2="15.01" y2="9" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
      <circle cx="12" cy="12" r="6" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
      <circle cx="12" cy="12" r="2" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
    </svg>
  );
}

function LightbulbIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 21h6" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M9 18h6" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M12 2a7 7 0 0 1 7 7c0 2.5-1.5 4.5-3 6H8C6.5 13.5 5 11.5 5 9a7 7 0 0 1 7-7z" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function Home() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.15 }
    );
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">

      {/* Nav */}
      <nav className="nav">
        <div className="nav-inner container">
          <div className="nav-logo">OurStory</div>
          <div className="nav-links">
            <a href="/guidelines">Guidelines</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-circle c1" />
          <div className="hero-circle c2" />
          <div className="hero-circle c3" />
        </div>
        <div className="container hero-inner">
          <div className="logo-wrap reveal">
            <img src={logo} alt="OurStory" className="hero-logo spinning" />
          </div>
          <h1 className="hero-title reveal">
            An Interactive Community Archive<br />
            and<br />
            Collaborative Journal
          </h1>
          <p className="hero-tagline reveal">Creating Memories. Together.</p>
          <p className="hero-sub reveal">Under your chosen alias, safely and with care.</p>
          <div className="hero-btns reveal">
            <a href="#download" className="btn-primary">Download the app</a>
            <a href="#how" className="btn-ghost">See how it works</a>
          </div>
        </div>
        <div className="hero-scroll">↓</div>
      </section>

      {/* Prompt preview */}
      <section className="prompt-section">
        <div className="container">
          <div className="prompt-card reveal">
            <div className="prompt-label">TODAY'S PROMPT</div>
            <p className="prompt-text">"What is a small moment from your childhood that you still think about today?"</p>
            <div className="prompt-stories">
              <div className="story-preview">
                <div className="story-meta">
                  <span className="story-alias">Marigold42</span>
                  <span className="story-time">2h ago</span>
                </div>
                <p>I remember my grandmother's kitchen on Sunday mornings — the smell of cardamom and the sound of her humming something I never learned the name of...</p>
                <div className="story-reactions">
                  <div className="reaction"><SmileyIcon /><span>Thanks for sharing</span></div>
                </div>
              </div>
              <div className="story-preview">
                <div className="story-meta">
                  <span className="story-alias">BlueSpruce7</span>
                  <span className="story-time">4h ago</span>
                </div>
                <p>There was a tree in our backyard with a low branch perfect for sitting. I'd go there when things got loud inside. I still think about that branch.</p>
                <div className="story-reactions">
                  <div className="reaction"><HeartIcon /><span>Sending you love</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="how" id="how">
        <div className="container">
          <h2 className="section-title reveal">How OurStory works</h2>
          <div className="steps">
            <div className="step reveal">
              <div className="step-num">01</div>
              <h3>Get a daily prompt</h3>
              <p>Everyday a new question arrives — crafted to help you reflect on what matters.</p>
            </div>
            <div className="step reveal">
              <div className="step-num">02</div>
              <h3>Share your memory</h3>
              <p>Write freely under your chosen alias. No likes, no followers — just your story, shared with care.</p>
            </div>
            <div className="step reveal">
              <div className="step-num">03</div>
              <h3>Read others' stories</h3>
              <p>Discover that your experiences are more universal than you think. React with warmth, not metrics.</p>
            </div>
            <div className="step reveal">
              <div className="step-num">04</div>
              <h3>Build your archive</h3>
              <p>Every story you share becomes part of your personal archive — a journal of your life in moments.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values">
        <div className="container">
          <h2 className="section-title reveal">Built differently</h2>
          <div className="values-grid">
            <div className="value-card reveal">
              <div className="value-icon"><SmileyIcon /></div>
              <h3>Alias-based by design</h3>
              <p>Your alias is all anyone sees. No real names, no profile photos — just your story.</p>
            </div>
            <div className="value-card reveal">
              <div className="value-icon"><TargetIcon /></div>
              <h3>Safe & moderated</h3>
              <p>AI-powered content moderation and a dedicated human review team keep our community kind.</p>
            </div>
            <div className="value-card reveal">
              <div className="value-icon"><HeartIcon /></div>
              <h3>No engagement metrics</h3>
              <p>No like counts, no follower numbers. Just stories and the people who need to hear them.</p>
            </div>
            <div className="value-card reveal">
              <div className="value-icon"><LightbulbIcon /></div>
              <h3>For ages 16+</h3>
              <p>OurStory is built for thoughtful reflection. We require age verification to protect our community.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download */}
      <section className="download" id="download">
        <div className="container">
          <div className="download-inner reveal">
            <img src={logo} alt="OurStory" className="download-logo" />
            <h2>Start sharing your story</h2>
            <p>Available now on iOS and Android.</p>
            <div className="download-btns">
              <a href="https://apps.apple.com/us/app/the-ourstory-app/id6759184164" className="store-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#1D492F">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span><small>Download on the</small>App Store</span>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.brianna.ourstory" className="store-btn">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#1D492F">
                  <path d="M3 20.5v-17c0-.83.94-1.3 1.6-.8l14 8.5c.6.36.6 1.24 0 1.6l-14 8.5c-.66.5-1.6.03-1.6-.8z"/>
                </svg>
                <span><small>Get it on</small>Google Play</span>
              </a>
            </div>
            <a href="https://www.instagram.com/theourstoryapp" target="_blank" rel="noreferrer" className="instagram-link">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="rgba(255,255,255,0.6)" stroke="none"/>
              </svg>
              Follow us on Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <img src={logo} alt="OurStory" className="footer-logo-img" />
          <div className="footer-links">
            <a href="/guidelines">Community Guidelines</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href="https://www.instagram.com/theourstoryapp?igsh=NTc4MTIwNjQ2YQ%3D%3D" target="_blank" rel="noreferrer">Instagram</a>
            <a href="mailto:info@theourstoryapp.com">Contact</a>
          </div>
          <p className="footer-copy">© 2026 OurStory. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
}
